//@ts-nocheck
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import PageWrapper from "../../../layout/PageWrapper/PageWrapper";
import Page from "../../../layout/Page/Page";
import Button from "../../../components/bootstrap/Button";
import Card, {
  CardActions,
  CardBody,
  CardHeader,
} from "../../../components/bootstrap/Card";
import Dropdown, {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "../../../components/bootstrap/Dropdown";
import LoaderWrapper from "../../../layout/Wrapper/LoaderWrapper";
import AddUserModal from "./AddUserModal";
import SearchBar from "../../../components/SearchBar";
import PaginationButtons from "../../../components/PaginationButtons";
import ConfirmationModal from "../ConfirmationModal";
import Checks from "../../../components/bootstrap/forms/Checks";
import Spinner from "../../../components/bootstrap/Spinner";
import userState from "../../../shared/mobx/user-state";
import subscriptionState from "../../../shared/mobx/subscription-state";

const ManualUsersPage = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sortField, setSortField] = useState<string>("createdAt");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>({});
  const [refreshKey, setRefreshKey] = useState(0);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [planList, setPlanList] = useState<any[]>([]);
  const [targetUser, setTargetUser] = useState<any>(null);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const refresh = () => setRefreshKey((v) => v + 1);

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      const res = await userState.getManualUserList({
        per_page: perPage,
        page: currentPage,
        search: searchQuery,
        order_by: sortOrder,
        order_by_field: sortField,
      });
      setUsers(res?.data || []);
      setTotalRecords(res?.total_records || 0);
      setLoading(false);
    };
    loadUsers();
  }, [currentPage, perPage, searchQuery, sortOrder, sortField, refreshKey]);

  useEffect(() => {
    const loadPlans = async () => {
      const plans = await subscriptionState.getSubscriptiondescriptionPlans(false);
      setPlanList(plans?.access || []);
    };
    loadPlans();
  }, []);

  const getDefaultFreeMonthlyPlan = () =>
    planList.find((plan) => {
      const title = `${plan?.title || plan?.name || ''}`.toLowerCase();
      const interval = `${plan?.interval || plan?.billing_cycle?.interval || ''}`.toLowerCase();
      return title.includes('free') && interval === 'month';
    }) || null;

  const formatPackageName = (packageName?: string) => {
    const normalized = `${packageName || ''}`.trim().toLowerCase();
    if (!normalized) {
      return "-";
    }
    if (normalized === "cancel" || normalized === "cancelled" || normalized === "canceled") {
      return "Cancelled";
    }
    return packageName;
  };

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder((p) => (p === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const openAddModal = () => {
    setSelectedUser({});
    setIsModalOpen(true);
  };

  const openEditModal = (user: any) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const openAssignModal = (user: any) => {
    setTargetUser(user);
    setSelectedPlanId("");
    setAssignModalOpen(true);
  };

  const resolveCreatedManualUserId = async (response: any, values: any) => {
    const responseUserId =
      response?.data?.id ||
      response?.data?.user?.id ||
      response?.user?.id;

    if (responseUserId) {
      return responseUserId;
    }

    const lookup = await userState.getManualUserList({
      per_page: 1,
      page: 1,
      search: values.email,
      order_by: "desc",
      order_by_field: "createdAt",
    });

    return lookup?.data?.[0]?.id || null;
  };

  const handleManualUserCreated = async (response: any, values: any) => {
    if (response?.data?.defaultPlanAssigned) {
      return;
    }

    const defaultPlan = getDefaultFreeMonthlyPlan();
    if (!defaultPlan?.plan_id) {
      return;
    }

    const createdUserId = await resolveCreatedManualUserId(response, values);
    if (!createdUserId) {
      return;
    }

    await userState.assignManualSubscription(createdUserId, defaultPlan.plan_id);
  };

  const handleAssignPackage = async () => {
    if (!targetUser?.id || !selectedPlanId) return;
    setAssigning(true);
    const ok = await userState.assignManualSubscription(targetUser.id, selectedPlanId);
    setAssigning(false);
    if (ok) {
      setAssignModalOpen(false);
      setTargetUser(null);
      refresh();
    }
  };

  const openCancelModal = (user: any) => {
    setTargetUser(user);
    setCancelModalOpen(true);
  };

  const openDeleteModal = (user: any) => {
    setTargetUser(user);
    setDeleteModalOpen(true);
  };

  const setLoader = (item: any, status = false) => {
    const updated = users.map((u) =>
      u.id === item.id ? { ...u, loading: status } : u
    );
    setUsers(updated);
  };

  const toggleUserStatus = async (item: any) => {
    await setLoader(item, true);
    await userState.toggleUserStatus(item);
    await setLoader(item, false);
    refresh();
  };

  const handleCancelPackage = async () => {
    if (!targetUser?.id) return;
    setCanceling(true);
    const ok = await userState.cancelManualSubscription(targetUser.id);
    setCanceling(false);
    if (ok) {
      setCancelModalOpen(false);
      setTargetUser(null);
      refresh();
    }
  };

  const handleDeleteUser = async () => {
    if (!targetUser?.id) return;
    setDeleting(true);
    await userState.deleteUser(targetUser.id);
    setDeleting(false);
    setDeleteModalOpen(false);
    setTargetUser(null);
    refresh();
  };

  return (
    <PageWrapper title="Manual Users">
      <Page container="fluid">
        <ConfirmationModal
          isOpen={cancelModalOpen}
          setIsOpen={setCancelModalOpen}
          loading={canceling}
          title="Cancel Subscription"
          message="Are you sure you want to cancel this user's package?"
          handleAction={handleCancelPackage}
        />

        <ConfirmationModal
          isOpen={deleteModalOpen}
          setIsOpen={setDeleteModalOpen}
          loading={deleting}
          title="Delete User"
          message="Are you sure you want to delete this manual user?"
          handleAction={handleDeleteUser}
        />

        <AddUserModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          refresher={refresh}
          user={selectedUser}
          mode="manual"
          onSuccess={handleManualUserCreated}
        />

        <div className="row">
          <div className="col-12">
            <div className="display-6 py-2 plan-heading d-flex justify-content-between align-items-center">
              <span>Manual Users</span>
              <CardActions>
                <Button onClick={openAddModal} color="info auth-btn">
                  Add Manual User
                </Button>
              </CardActions>
            </div>
          </div>

          <div className="col-12">
            <Card>
              <CardHeader borderSize={1}>
                <div className="search-wrap">
                  <span className="fw-bold fs-4">Manual Users</span>
                  <div
                    className="d-flex justify-content-end"
                    style={{ minWidth: "280px", maxWidth: "360px", width: "100%" }}
                  >
                    <SearchBar
                      fetchData={() => {}}
                      placeholder="Search Manual User"
                      onSearch={(q: string) => {
                        setSearchQuery(q);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardBody className="manual-users-table-wrap">
                <LoaderWrapper isLoading={loading} data={users}>
                  <>
                    <table className="table table-modern">
                      <thead>
                        <tr>
                          <th role="button" onClick={() => toggleSort("firstName")}>
                            First Name
                          </th>
                          <th role="button" onClick={() => toggleSort("lastName")}>
                            Last Name
                          </th>
                          <th role="button" onClick={() => toggleSort("email")}>
                            Email
                          </th>
                          <th role="button" onClick={() => toggleSort("package_name")}>
                            Package
                          </th>
                          <th role="button" onClick={() => toggleSort("createdAt")}>
                            Creation Date
                          </th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.length > 0 ? (
                          users.map((item) => (
                            <tr key={item.id}>
                              <td>{item.firstName}</td>
                              <td>{item.lastName}</td>
                              <td>{item.email}</td>
                              <td>{formatPackageName(item.package_name)}</td>
                              <td>{dayjs(item.createdAt).format("MM-DD-YYYY")}</td>
                              <td>
                                {item.loading ? (
                                  <Spinner size="12px" color="primary" />
                                ) : (
                                  <Checks
                                    id={`manual-user-status-${item.id}`}
                                    name={String}
                                    className="status"
                                    type="switch"
                                    onChange={() => toggleUserStatus(item)}
                                    checked={item.status === "Active"}
                                  />
                                )}
                              </td>
                              <td>
                                <Dropdown>
                                  <DropdownToggle hasIcon={false}>
                                    <Button
                                      icon="MoreHoriz"
                                      aria-label="More options"
                                    />
                                  </DropdownToggle>
                                  <DropdownMenu isAlignmentEnd>
                                    <DropdownItem>
                                      <Button
                                        onClick={() => openEditModal(item)}
                                        icon="Edit"
                                      >
                                        Edit
                                      </Button>
                                    </DropdownItem>
                                    <DropdownItem>
                                      <Button
                                        onClick={() => openAssignModal(item)}
                                        icon="LocalOffer"
                                      >
                                        Assign / Change Package
                                      </Button>
                                    </DropdownItem>
                                    <DropdownItem>
                                      <Button
                                        onClick={() => openCancelModal(item)}
                                        icon="Delete"
                                      >
                                        Cancel Package
                                      </Button>
                                    </DropdownItem>
                                    <DropdownItem>
                                      <Button
                                        onClick={() => openDeleteModal(item)}
                                        icon="DeleteForever"
                                      >
                                        Delete
                                      </Button>
                                    </DropdownItem>
                                  </DropdownMenu>
                                </Dropdown>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={7} className="text-center">
                              No manual users found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <PaginationButtons
                      label="items"
                      setCurrentPage={setCurrentPage}
                      totalItems={totalRecords}
                      setPerPage={setPerPage}
                      currentPage={currentPage}
                    />
                  </>
                </LoaderWrapper>
              </CardBody>
            </Card>
          </div>
        </div>

        {assignModalOpen && (
          <div className="modal show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Assign Package</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setAssignModalOpen(false)}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-2">
                    User: <strong>{targetUser?.email}</strong>
                  </p>
                  <label className="form-label">Select Package</label>
                  <select
                    className="form-select"
                    value={selectedPlanId}
                    onChange={(e) => setSelectedPlanId(e.target.value)}
                  >
                    <option value="">Select package</option>
                    {planList.map((plan) => (
                      <option key={plan.plan_id} value={plan.plan_id}>
                        {plan.title} ({plan.interval})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="modal-footer">
                  <Button
                    color="secondary"
                    className="auth-btn text-capitalize"
                    onClick={() => setAssignModalOpen(false)}
                  >
                    Close
                  </Button>
                  <Button
                    color="primary"
                    className="auth-btn"
                    isDisable={assigning || !selectedPlanId}
                    onClick={handleAssignPackage}
                  >
                    {assigning ? "Please wait..." : "Save Package"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Page>
    </PageWrapper>
  );
};

export default ManualUsersPage;
